import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip } from '@mui/material';
import { useAuthContext } from '../../contexts/authContext';
import useSnackbar from '../../hooks/useSnackbar';
import { Severity } from '../../types';
import { FFTable, FFTableColumn } from '../../components/FFTable';
import {
  DUE_DATE_OPTIONS,
  ListFilterBar,
  ListFilters,
  SelectFilter,
} from '../../components/ListFilterBar/ListFilterBar';
import { PAYMENT_STATUS_OPTIONS } from './PaymentsPage';
import {
  getAllPaymentRequests,
  markPaymentAsPaid,
  undoPaymentAsPaid,
} from '../../services/financeService';
import { PaymentRequest, PaymentRequestsSummary } from './types/finance.types';
import PaymentsSummaryCards, { rowBalance } from './components/PaymentsSummaryCards';
import { FairFareQueryParams } from 'types/interfaces/shared';
import TenantSelector from '../../components/TenantSelector';
import { ConfirmationDialog } from '../Claims/components/ConfirmationDialog';
import { MDButton } from 'components/MDButton';

type PaymentsPageData = {
  entities: PaymentRequest[];
  isLoading: boolean;
  error: null | Error;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

type QueryState = {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

type ProcessFilters = ListFilters & { status: string };

// This page is the "to be paid" queue, so it opens on open requests.
const DEFAULT_PROCESS_FILTERS: ProcessFilters = {
  search: '',
  status: 'Open',
  datePreset: 'all',
};

const PROCESS_SELECTS: SelectFilter[] = [
  {
    key: 'status',
    id: 'process-payments-status',
    label: 'Status',
    options: [...PAYMENT_STATUS_OPTIONS, { value: '', label: 'All statuses' }],
  },
];

const listQuery = (f: ProcessFilters) => ({
  fromDate: f.fromDate,
  untilDate: f.untilDate,
  status: f.status || undefined,
  // Dates here are due dates, not the date the request was created.
  filters: { search: f.search, dateField: f.fromDate || f.untilDate ? 'due' : undefined },
});

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const ProcessPaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [currentTenant, setCurrentTenant] = useState<number>(0);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [pageData, setPageData] = useState<PaymentsPageData>({
    entities: [],
    isLoading: true,
    error: null,
    pagination: {
      page: 1,
      pageSize: 100,
      total: 0,
      totalPages: 0,
    },
  });
  const [queryState, setQueryState] = useState<QueryState>({
    page: 1,
    pageSize: 100,
    sortBy: 'paymentDueDate',
    sortOrder: 'desc',
  });
  const [filters, setFilters] = useState<ProcessFilters>(DEFAULT_PROCESS_FILTERS);
  // Only the first load replaces the page with a loading message; after that
  // the filter bar stays mounted so a search never loses its cursor.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [summary, setSummary] = useState<PaymentRequestsSummary | undefined>();
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'mark-paid' | 'undo-payment' | null;
    paymentRequestId: string | null;
  }>({ open: false, action: null, paymentRequestId: null });
  const show = useSnackbar();

  const { role } = user || { role: '' };

  const handleMarkAsPaid = async (paymentRequestId: string) => {
    setProcessingId(paymentRequestId);
    try {
      await markPaymentAsPaid(paymentRequestId);
      show('Payment marked as paid successfully', Severity.success);
      await fetchDataWithQuery(queryState);
    } catch (error) {
      show('Failed to mark payment as paid', Severity.error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleUndoPayment = async (paymentRequestId: string) => {
    setProcessingId(paymentRequestId);
    try {
      await undoPaymentAsPaid(paymentRequestId);
      show('Payment unmarked successfully', Severity.success);
      await fetchDataWithQuery(queryState);
    } catch (error) {
      show('Failed to undo payment', Severity.error);
    } finally {
      setProcessingId(null);
    }
  };

  const openMarkAsPaidConfirm = (paymentRequestId: string) => {
    setConfirmDialog({ open: true, action: 'mark-paid', paymentRequestId });
  };

  const openUndoPaymentConfirm = (paymentRequestId: string) => {
    setConfirmDialog({ open: true, action: 'undo-payment', paymentRequestId });
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog.paymentRequestId || !confirmDialog.action) return;
    setConfirmDialog((prev) => ({ ...prev, open: false }));
    if (confirmDialog.action === 'mark-paid') {
      await handleMarkAsPaid(confirmDialog.paymentRequestId);
    } else if (confirmDialog.action === 'undo-payment') {
      await handleUndoPayment(confirmDialog.paymentRequestId);
    }
    setConfirmDialog({ open: false, action: null, paymentRequestId: null });
  };

  const handleCancelAction = () => {
    setConfirmDialog({ open: false, action: null, paymentRequestId: null });
  };

  const columns: FFTableColumn<PaymentRequest>[] = [
    {
      id: 'requestId',
      label: t('Payment ID'),
      width: 150,
      type: 'text',
      filterable: true,
    },
    {
      id: 'paymentDueDate',
      label: t('Due Date'),
      type: 'date',
      sortable: true,
      renderCell: (value) => {
        return new Date(value).toLocaleDateString();
      },
    },
    {
      id: 'status',
      label: t('Status'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        const status = value as string;
        let color: 'default' | 'warning' | 'success' | 'primary' | 'error' = 'default';
        
        if (status === 'Open') {
          color = 'warning';
        } else if (status === 'Paid') {
          color = 'success';
        } else if (status === 'Invoiced') {
          color = 'primary';
        } else if (status === 'Void') {
          color = 'error';
        }
        
        return <Chip label={status} color={color} size="small" />;
      },
    },
    {
      id: 'totalAllowed',
      label: t('Total Allowed'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{formatter.format(value)}</span>;
      },
    },
    {
      id: 'totalCostContainmentFees',
      label: t('Total Fee'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{formatter.format(value)}</span>;
      },
    },
    {
      // What is still owed on this request; Paid and Void owe nothing.
      id: 'balance',
      label: t('Balance'),
      type: 'text',
      sortable: false,
      renderCell: (value, row) => <span>{formatter.format(rowBalance(row))}</span>,
    },
    {
      id: 'actions',
      label: t('Actions'),
      type: 'custom' as const,
      width: 200,
      renderCell: (value: any, row: PaymentRequest) => {
        if (row.status === 'Open') {
          return (
            <MDButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openMarkAsPaidConfirm(row.requestId)}
              disabled={processingId === row.requestId}
            >
              Mark as Paid
            </MDButton>
          );
        } else if (row.status === 'Paid') {
          return (
            <MDButton
              variant="outlined"
              color="error"
              size="small"
              onClick={() => openUndoPaymentConfirm(row.requestId)}
              disabled={processingId === row.requestId}
            >
              Undo Payment
            </MDButton>
          );
        }
        return null;
      },
    },
  ];

  const fetchDataWithQuery = useCallback(
    async (query: QueryState) => {
      try {
        setPageData((prev) => ({ ...prev, isLoading: true }));

        const params: FairFareQueryParams = {
          page: query.page,
          pageSize: query.pageSize,
          sortBy: query.sortBy,
          sortOrder: query.sortOrder,
          ...listQuery(filters),
        };

        const response = await getAllPaymentRequests(params, currentTenant || undefined);

        setPageData({
          entities: response.paymentRequests,
          isLoading: false,
          error: null,
          pagination: response.pagination,
        });
        setHasLoaded(true);
        setSummary(response.summary);

        setQueryState(query);
      } catch (error) {
        const e = error as Error;
        setPageData((prev) => ({
          ...prev,
          isLoading: false,
          error: e,
        }));
        show('Failed to load payment requests data', Severity.error);
      }
    },
    [show, filters, currentTenant]
  );

  // Any change to what is being asked for starts again from the first page.
  // Keyed on the inputs rather than the callback, whose identity follows the
  // snackbar's `show` and would otherwise reload after every message.
  useEffect(() => {
    fetchDataWithQuery({ ...queryState, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentTenant]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: newPage,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: 1,
        pageSize: newPageSize,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  if (!user) {
    return <div>{t('Loading')}..</div>;
  }

  if (!hasLoaded && pageData.isLoading) {
    return <div>{t('Loading')}..</div>;
  }

  if (pageData.error) {
    return (
      <div>
        {t('Error loading payment requests data')}: {pageData.error.message}
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: '100vw',
        margin: '0 auto',
        paddingTop: '10px',
        overflow: 'auto',
      }}
    >
      <Box mx={2}>
        <PaymentsSummaryCards summary={summary} />
        <ListFilterBar
          filters={filters}
          defaults={DEFAULT_PROCESS_FILTERS}
          onChange={setFilters}
          total={pageData.pagination.total}
          noun="payments"
          searchPlaceholder="Search by payment ID, bill ID or carrier"
          selects={PROCESS_SELECTS}
          dateLabel="Due Date"
          dateOptions={DUE_DATE_OPTIONS}
          tenantSlot={
            <TenantSelector
              value={currentTenant}
              onChange={(val) => setCurrentTenant(val)}
              label={t('Tenant')}
              compact
            />
          }
        />
      </Box>
      <FFTable
        columns={columns}
        rows={pageData.entities}
        pagination={{
          page: pageData.pagination.page,
          pageSize: pageData.pagination.pageSize,
          total: pageData.pagination.total,
          totalPages: pageData.pagination.totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [50, 100, 200],
        }}
        serverSide={true}
        loading={pageData.isLoading}
        error={pageData.error}
        // Headers used to move the arrow without re-sorting anything.
        onSortChange={(orderBy, order) => {
          fetchDataWithQuery({ ...queryState, page: 1, sortBy: orderBy, sortOrder: order });
        }}
        initialSort={{
          orderBy: queryState.sortBy || 'paymentDueDate',
          order: queryState.sortOrder || 'desc',
        }}
        rowKey="id"
        emptyStateMessage={t('No payment requests found')}
        errorStateMessage={t('Error loading payment requests data')}
        loadingStateMessage={t('Loading payment requests data...')}
      />
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.action === 'mark-paid' ? 'Mark as Paid' : 'Undo Payment'}
        message={
          confirmDialog.action === 'mark-paid'
            ? 'Are you sure you want to mark this payment as paid? This will update all linked bills.'
            : 'Are you sure you want to revert this payment to Open status?'
        }
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        confirmText="Confirm"
        cancelText="Cancel"
        loading={!!processingId}
      />
    </div>
  );
};
